/**
 * Onboarding setup normalization for the owned clients_params values Nexus writes.
 * This runs before the existing Nexus setup schema, on the form and on the server alike, and is
 * safe to run twice (the worker validates the API's already-normalized values again).
 *
 * It mirrors how the core pipeline reads a clients_params row: a blank or JSON `null` list is an
 * empty list, a blank or JSON `null` object is an empty object, JSON text is parsed, a blank checkbox
 * is unset, and a `YYYY-MM-DD` start date is read as its month. It never loosens a Nexus rule: Meta
 * IDs, the TS start range, the tagging sector and the allowed options are still checked by the schema.
 */

export type OnboardingNormalizeResult<T> = { ok: true; value: T } | { ok: false; reason: string };

const ONBOARDING_LIST_FIELDS = [
    'nc_names',
    'custom_pixel',
    'google_account_ids',
    'google_custom_event',
    'nc_columns_level',
] as const;

const ONBOARDING_CHECKBOX_FIELDS = ['position_nc', 'overwrite_conversion', 'overwrite_product', 'low_memory_version'] as const;

/** Text inputs restricted to fixed options, where a blank value means "use the default". */
const ONBOARDING_OPTION_FIELDS = ['front_product_column'] as const;

/** `YYYY-MM-DD`, optionally followed by a time, which is all the pipeline parses (`str(value)[:10]`). */
const TS_START_TEXT = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/;

const TS_START_TEXT_REASON = 'Must be a real date in YYYY-MM-DD format';

export function normalizeOnboardingList(value: unknown): OnboardingNormalizeResult<unknown[]> {
    const parsed = parseListInput(value);
    if (!parsed.ok) return parsed;
    return { ok: true, value: parsed.value.flatMap(toListItem) };
}

export function normalizeOnboardingNcDict(value: unknown): OnboardingNormalizeResult<Record<string, unknown>> {
    if (isBlank(value)) return { ok: true, value: {} };
    if (typeof value === 'string') {
        const parsed = parseJson(value.trim());
        if (!parsed.ok) return { ok: false, reason: 'Must be an object' };
        return parsed.value === null ? { ok: true, value: {} } : asObject(parsed.value);
    }
    return asObject(value);
}

/**
 * Parse a JSON list string for `account_id`. A blank value, or JSON `null`, becomes an empty list so
 * the schema reports the missing account rather than a type error; Meta IDs stay required.
 */
export function normalizeAccountIdInput(value: unknown): OnboardingNormalizeResult<unknown> {
    if (value === undefined) return { ok: true, value };
    const parsed = parseListInput(value);
    if (!parsed.ok) return parsed;
    return { ok: true, value: parsed.value.flatMap(toListItem) };
}

/**
 * Returns a copy of a setup object with the pipeline's list / dict / blank normalization applied.
 * Non-objects are returned unchanged so the Nexus schema can reject them.
 */
export function normalizeOnboardingSetup(input: unknown): { ok: true; value: unknown } | { ok: false; field: string; reason: string } {
    if (input === null || typeof input !== 'object' || Array.isArray(input)) return { ok: true, value: input };
    const source = input as Record<string, unknown>;
    const next: Record<string, unknown> = { ...source };

    if ('account_id' in source) {
        const accountId = normalizeAccountIdInput(source.account_id);
        if (!accountId.ok) return { ok: false, field: 'account_id', reason: accountId.reason };
        next.account_id = accountId.value;
    }

    for (const field of ONBOARDING_LIST_FIELDS) {
        if (!(field in source)) continue;
        const normalized = normalizeOnboardingList(source[field]);
        if (!normalized.ok) return { ok: false, field, reason: normalized.reason };
        next[field] = normalized.value;
    }

    if ('nc_dict' in source) {
        const dict = normalizeOnboardingNcDict(source.nc_dict);
        if (!dict.ok) return { ok: false, field: 'nc_dict', reason: dict.reason };
        next.nc_dict = dict.value;
    }

    // A blank checkbox is unset, so the schema's default (false) applies. Text such as "true" is
    // still rejected: Nexus does not guess a checkbox from text.
    for (const field of ONBOARDING_CHECKBOX_FIELDS) {
        if (field in source && isBlank(source[field])) delete next[field];
    }

    for (const field of ONBOARDING_OPTION_FIELDS) {
        if (field in source && isBlank(source[field])) next[field] = null;
    }

    if (typeof source.ts_start === 'string') {
        const month = toTsStartMonth(source.ts_start);
        if (!month) return { ok: false, field: 'ts_start', reason: TS_START_TEXT_REASON };
        next.ts_start = month;
    }

    return { ok: true, value: next };
}

/**
 * The month of a `YYYY-MM-DD` text date, or null when that day does not exist (`2026-10-99`,
 * `2026-02-29`), matching the pipeline's `strptime(value[:10], "%Y-%m-%d")`.
 */
function toTsStartMonth(text: string): { year: number; month: number } | null {
    const parts = TS_START_TEXT.exec(text.trim());
    if (!parts) return null;
    const [year, month, day] = [Number(parts[1]), Number(parts[2]), Number(parts[3])];
    const date = new Date(Date.UTC(year, month - 1, day));
    // Date.UTC rolls an impossible day into the next month, so the round trip catches it.
    const isRealDay = date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
    return isRealDay ? { year, month } : null;
}

/** A blank value, or JSON `null`, is an empty list; JSON text is parsed; anything but a list is refused. */
function parseListInput(value: unknown): OnboardingNormalizeResult<unknown[]> {
    if (isBlank(value)) return { ok: true, value: [] };
    if (Array.isArray(value)) return { ok: true, value };
    if (typeof value !== 'string') return { ok: false, reason: 'Must be a list' };
    const parsed = parseJson(value.trim());
    if (!parsed.ok) return { ok: false, reason: 'Must be a list' };
    if (parsed.value === null) return { ok: true, value: [] };
    return Array.isArray(parsed.value) ? { ok: true, value: parsed.value } : { ok: false, reason: 'Must be a list' };
}

/** A number is read as its text (pixel and account IDs often arrive as numbers); a blank item is dropped. */
function toListItem(item: unknown): unknown[] {
    if (typeof item === 'number' && Number.isFinite(item)) return [String(item)];
    if (isBlank(item)) return [];
    return [item];
}

function isBlank(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' && value.trim().length === 0);
}

function parseJson(text: string): OnboardingNormalizeResult<unknown> {
    try {
        return { ok: true, value: JSON.parse(text) as unknown };
    } catch {
        return { ok: false, reason: 'Must be valid JSON' };
    }
}

function asObject(value: unknown): OnboardingNormalizeResult<Record<string, unknown>> {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return { ok: false, reason: 'Must be an object' };
    }
    return { ok: true, value: value as Record<string, unknown> };
}
